<template>
  <div>
    <header>
      <div id="hero-video">
        <div class="overlay"></div>
        <video id="video" preload="auto" autoplay loop muted playsinline>
          <source src="@/assets/barcelona_muted_compressed.mp4" type="video/mp4" />
        </video>
      </div>

      <!-- <div id="logo">
        <router-link to="/">isoquanta</router-link>
      </div>-->

      <div class="intro-title-container">
        <h1 class="intro-title">
          Catalonia
          <br />at the crossroads
        </h1>
        <p class="intro-subtitle">
          Innovation Districts and Industrial Clusters
          <br />in the Barcelona
          Metropolitan Region
        </p>
        <p class="company-name">ARETIAN</p>
        <p class="company-description">Urban Analytics and Design</p>
      </div>
      <div class="best-viewed">
        <p class="best-viewed">
          Best viewed on Desktop
        </p>
      </div>
    </header>

    <main>
      <section>
        <h1>Designing cities for the future</h1>
        <p class="author">
          by Jeremy Burke, Ramon Gras and Fernando Yu
        </p>
        <p>
          Our society is facing three important challenges: growing income inequality,
          sustainability and health concerns due to climate change, and the accelerating effects
          of the digital revolution, which threatens to erode the way we work and relate to one
          another. Meanwhile, more people are choosing to live in urban environments. These
          trends highlight the importance of urban design, organizational structures, incentive
          schemes, economic development patterns, and knowledge networks, as areas of rapidly
          emerging risks and also of extraordinary opportunities.
        </p>
        <p>
          Inequality rates are greater than ever before. In the 18th century, the wealthiest
          nation in the world per capita, the Netherlands, was 4 times more prosperous than the
          poorest country in the world. At the beginning of the XXI Century, the wealthiest
          countries are now 200 times wealthier per capita than the poorest. This dramatic shift
          of wealth from the hands of many to the hands of the few has reduced the proper
          planning of resources to help support life of mankind as informal settlements grow.
          International migrations are on the rise, eventually inducing social and political
          distress, as people search for new opportunities to survive. Without proper living
          environments, people will lack the structure on which to build their lives, their
          economies, and their futures.
        </p>
        <p>
          Thoughtful design of our urban environments is paramount as we foster and shape our
          relationship with the natural world around us and create healthy sustainable spaces. As
          the built environment accounts for more than 30% of the total energy use of the planet,
          it is critical that we address how to best organize our urban systems to decrease total
          energy consumption, create walkable and livable neighborhoods, and increase access to
          desirable amenities and employment opportunities.
        </p>
        <p>Moreover, today’s urban design decisions will shape the future of work and the economic vitality of our cities. We can expect today’s jobs to change dramatically with the introduction of cognitive automation by means of artificial intelligence, robotics, and data-driven decision support systems. While these new tools offer many opportunities to help us solve complex problems, they must also be designed and distributed in a way that does not exacerbate the already-severe income inequality between the rich and the poor. Future prosperity will depend on increasing education among the entire population, as well as upskilling and reskilling traditional workers to ease the transition into the digital economy. It will take conscious effort to prevent large sections of our society from being left behind as the world moves ever faster toward digital solutions.</p>
        <h2>A roadmap for Barcelona</h2>
        <p>Thus, for the Barcelona Metropolitan Region to be well-equipped to successfully tackle the societal challenges of the 21st Century, in terms of high quality employment opportunities, decreasing unemployment rates, generating distributed prosperity, and providing a societal response to the job disruption caused by the emergence of AI, robotics, and automation, in a context of increasingly tight global economic competitiveness, the Barcelona Metropolitan Region needs to develop an advanced understanding of its strengths and weaknesses, risks and opportunities, to be prepared to make the best decisions, and support a citizen-centric economy, able to create economic development opportunities and reduce social vulnerability.</p>
        <p>
          <a
          href="https://pemb.cat/public/docs/102_iw_barcelona_metropolitan_region__final_report.pdf">The
          present study</a> aims to describe the current state of current and up-and-coming
          innovation districts and industry clusters in the Barcelona Metropolitan Region,
          present benchmark metrics with respect to best cases in the industry, and preliminarily
          define policies, programmes, and pathways to distributed prosperity.
        </p>
      </section>
      <!-- <figure style="position:relative; width:984px; text-align:center"> -->
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/clusters.png" style="width:900px" />
        <figcaption style="margin-top:2rem">Industrial clusters in Barcelona Metropolitan region</figcaption>
      </figure>
      <section>
        <h2>Targeting Innovation Districts</h2>
        <p>
          An Innovation District is a specific geographic location, generally within cities,
          where high concentrations of people work in advanced industries in conjunction with
          other related companies and institutions. They also provide a mix of attractive
          amenities, housing options, and public spaces to give back to the community. In
          total, Innovation Districts enable greater collaboration, job opportunities, and
          regional competitiveness through their concentrated activities to unleash the latent
          potential for knowledge-intensive activities and respond to multiple societal
          problems.
        </p>
        <p>
          Given their ability to generate high quality employment opportunities, we observe a
          trend in many large or mid-scale metropolitan areas to build the most successful
          Innovation Districts in order to create the most welcoming and supportive environment
          to attract talented people, enable them to thrive, offer them healthy living
          environments, while boosting the positive impact that these concentrated, knowledge
          intensive activities can bring to communities.
        </p>
        <p>
          Previous literature has failed to create insightful results when attempting to describe
          local and regional innovation activities. Without granular data, and new methods of
          information segmentation, it is not possible to create new metrics capable of
          highlighting business and industry innovation intensity, performance, and impact for
          the broader society. In order to describe the complex nature of social organization,
          <span
            class="company-name"
          >ARETIAN</span> has created new models that help to break the complex problem into smaller
          pieces at different scales and timeframes
          and describe innovation activities of four and a half
          million people focusing on smaller subgroups that can be further studied, analyzed, and
          compared to one another. Through <a href="https://cities-today.com/how-to-better-understand-the-science-of-cities/">the following
          <span
            class="company-name"
          >ARETIAN</span> framework</a>, preliminary results
          and insights of the Metropolitan Barcelona Regional Area’s innovation economy are
          presented with guides for how to support their further design and development.
        </p>
        <h2>The Atlas of Innovation Districts</h2>
        <p>The first version of the <a href="https://www.aretian.com/atlas">Atlas of Innovation Districts</a> describes the network of knowledge in the United States, emphasizing the contribution of Innovation Districts as key drivers for high quality employment opportunity creation. Subsequent versions of the Atlas will incorporate Europe, Asia, Latin America, Africa and the Middle East, and Oceania to complete the picture of the Atlas of Global Innovation.</p>
        <p><a href="https://www.technologyreview.es/s/11355/hacia-una-nueva-ciencia-para-entender-y-disenar-mejor-las-ciudades">By analyzing the 50 most well known Innovation Districts in the United States</a>, we developed the first ever proprietary database, analytics engine, fundamental metrics, and visualizations to describe knowledge-intensive urban ecosystems in their complexity. As a consequence of creating this model, the team has also illuminated hundreds more, which were previously unknown or not considered to be an Innovation District.</p>
        <p>From the model, it is clear that innovating is hard, but it definitely pays off: currently Innovation Districts host slightly more than 3 million jobs, of which nearly 998k (32%) have an innovation-related component, as compared to an average business neighbourhood, where only 17% of employees have knowledge-intensive jobs.</p>
        <p>On a per resident basis, Innovation Districts host 9 times more employees, accommodate 15 times more innovation-related jobs, and generate 20 times more wealth than average neighbourhoods. Innovation Districts tend to be dramatically denser than the national average: they concentrate 26 times more density of jobs per square mile.</p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/industry_clusters.png" style="width:900px" />
        <figcaption
          style="margin-top:2rem"
        >Industrial corridors surrounding the Barcelona Metropolitan Region</figcaption>
      </figure>
      <section>
        <p>There are 25 innovation districts and industry clusters operating in the Barcelona Metropolitan Region. Each district was identified through tagging and clustering analysis based on their geographic proximity, industry relatedness, organizational affinity, and technology compatibility.</p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/strategic_districts.png" style="width:900px" />
        <figcaption style="margin-top:2rem">
          25 Strategic Innovation and Industrial Districts in the
          Barcelona Metropolitan Region
        </figcaption>
      </figure>
      <section>
        <p>The industrial cluster is the dominant typology with 19 clusters in the region, followed by four non-consolidated Research-led innovation districts, all of which are hosted in the City of Barcelona. There were only singular cases of the City Government-led innovation district, (22@), non-consolidated Strategic Governmental Agency-led (Parc Tecnològic del Vallès / Sincrotró Alba), and an early stage Entrepreneurial Hub (Pier01).</p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/by_institution.png" style="width:900px" />
        <figcaption style="margin-top:2rem">
          Classification of the 25 Strategic Innovation and
          Industrial Districts
        </figcaption>
      </figure>
      <section>
        <p>
          The comparisons between the innovation districts in the Barcelona Metropolitan Region
          and the Top 50 Innovation Districts in the US reveal that the Barcelona Metropolitan
          Region has had remarkable success in consolidating the City Government-led 22@
          Innovation District in less than two decades, because it has a much higher innovation
          intensity (44.85%) than its counterparts in the US, such as Boston Seaport (21.54%),
          Seattle South Lake (25.45%), or Saint Louis Cortex (22.4%). However, the 22@ is the
          single case of a consolidated City Government-led innovation district in the Barcelona
          Metropolitan Area in terms of both critical mass, innovation intensity and societal
          benefits. The 22@, however, falls short of its potential impact to the greater
          metropolitan area as it was designed around 5 unique industry-specific clusters, rather
          than those present in the regional cities around Barcelona. This lack of a strong
          relationship with the traditional export-driven products that support a majority of the
          economy in the region, limits the potential positive societal impact of 22@ beyond the
          boundaries of the Poblenou neighbourhood.
        </p>
        <h2>The trade hub of Catalonia</h2>
        <p>
          <span class="company-name">ARETIAN</span> has developed a mobility and export flow map
          for Catalonia, using Complexity Analysis and Network Theory. The pioneering study
          analyzes the state of the economy and exports in Catalonia, focusing on the levels of
          innovation by firms, the flow of products across different means of transport (air,
          water, road and railway) and the flow hierarchy according to network theory. The result
          of the analysis offers a new perspective of catalan exports and its interconnections,
          which allows for precise forecasts to optimize opportunities and minimize risks.
        </p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/catalonia_export_analysis.png" style="width:900px" />
        <figcaption style="margin-top:2rem">Catalonia: Export flow by value and weight</figcaption>
      </figure>
      <section>
        <p>
          Catalan exports have tripled in value between 1995 and 2015. However, the influence of the
          Barcelona hinterland as an intermodal hub for trade is still limited to the Metropolitan
          Region of Barcelona. This evolution is in stark contrast with the great potential of the
          region to become the capital of a continental region that extends from Lyon and Torino to
          Alicante, traversing Toulouse, Valencia and Zaragoza.
        </p>
        <p>
          In order to achieve its full potential, Catalonia has to adjust its strategy to boost the
          economy in the seven subregions: Barcelona, Corona Metropolitana, Regió Metropolitana,
          Camp de Tarragona - Penedès, Catalonia Central - Pirineus, Girona - Costa Brava and Plana
          de Lleida - Terres de l'Ebre.
        </p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/catalonia_exports_byport.png" style="width:900px" />
        <figcaption
          style="margin-top:2rem"
        >Barcelona exports network flow by road system (monetary value)</figcaption>
      </figure>
      <section>
        <h2>Mediterranean Railway Corridor</h2>
        <p>
          Currently, catalan exports by rail amount to 3% of total exports, compared to 27% of
          the median european levels.
          <span
            class="company-name"
          >ARETIAN</span>'s analysis
          estimates that the development of a Mediterranean Railway Corridor in Catalonia could
          encompass 46% of catalan exports or 65% of those completed by road. The railway flow
          could increase upwards of 16 times compared to the current levels, avoiding the
          circulation of 5000 trucks per day. This would imply a massive reduction in CO2
          emissions. As a result, the initial investment would be recovered in five years.
        </p>
        <p>
          The clustering analysis reveals that the type of products that are exported by road and
          are likely to being exported through the Mediterranean Railway Corridor are, in order of
          relevance: cars and automobile components, chemical and pharmaceutical products, plastic
          derivatives, agricultural products, textile products, metal and manufacturing products,
          industrial machinery, wood and carpentry products and precision instruments. The study has
          identified four main nodes to locate the future intermodal connectivity stations:
          Tarragona, with an Industrial Petrochemical and Renewable Energy Complex; the Dic Sud of
          the Port of Barcelona, with an industrial terminal of high value added; the Metropolitan
          Railway Hub of Llobregat; and the Agrifood Terminal of Penedes.
        </p>
        <p>
          Catalonia concentrates 25% of all Spanish exports, making it the export motor of Spain,
          and exports have been growing in the past years. However, in per capita terms Catalonia
          has lower export levels than other European countries.
          Moreover, the main market for catalan exports is Europe, with 68% of the total Catalan
          exports. The main transport mode is road, with 69% of the total, followed by a 22% by
          sea through the ports of Barcelona and Tarragona.
        </p>
        <p>
          The port of Barcelona has a great potential to transform into the main port of the
          Mediterranean due to its geographic position and the economic and industrial structure
          of the hinterland. Moreover, it has been recognized in recent years as one of the most
          innovative in terms of its mechanisms of internal efficiency and optimization. However,
          according to
          <span
            class="company-name"
          >ARETIAN</span>'s analysis, there is still ample
          room for improvement and opportunity to be the leader in the transport of goods in the
          Mediterranean.
        </p>
        <h2>Catalonia at the crossroads</h2>
        <p>
          <a href="https://pemb.cat/public/docs/102_iw_barcelona_metropolitan_region__final_report.pdf">The analysis of innovation intensity</a> reveals that the Barcelona Metropolitan Region
          is dominated by the third phase of urban innovation: advanced mass production. The
          Research and Academia as well as the Technology Transfer phases are remarkably weaker
          than their counterparts in the US, therefore, there is ample room to deploy specific
          policies, investment prioritization, academic programmes, and urban development
          opportunities to support and strengthen both research-oriented innovations and
          technology transfer processes.
        </p>
      </section>
      <figure style="display:block; margin-left:auto; margin-right: auto;">
        <img src="@/assets/innovation_districts.png" style="width:900px" />
        <figcaption
          style="margin-top:2rem"
        >
        Innovation Districts and Industrial Clusters in the Barcelona Metropolitan Region Based on their Level of Maturity
        </figcaption>
      </figure>
      <section>
        <p>
          There is an emerging Research “boomerang” conformed by 3 potential innovation
          districts: BSC-UPC, AltaDiagonal, and BarcelonaZonaAlta. A number of the most demanding
          Catalan and Spanish Universities are located in this stripe, yet their compounded
          innovation intensity rates are rather low (19%) compared to their American counterparts
          (39%). Such metrics reveal plenty of room for academic programmes, technology transfer
          platforms, and initiatives to be implemented to liberate the latent innovation forces
          within the community.
        </p>
        <p>
          Finally,
          <span class="company-name">ARETIAN</span>'s study concludes that the Port of Barcelona can
          significantly increase its potential after embarking in infrastructure and operational
          investment. From all the shipments to Asia from Catalonia, 35% is accomplished through
          other ports like Rotterdam, Antwerp, Hamburg and Bremen. In addition, the negative trade
          balance with China threatens to undermine the competitiveness of the Catalan industry in
          the medium run.
        </p>
        <p>
          Improvements in infrastructure would allow for an improvement in the logistic system of
          the Port of Barcelona and its hinterland, resulting in increased exports of Catalan
          firms to China and the rest of Asia. Some of the measures needed involve investment in
          port access and industrial docks to add value to goods imported from the Asian market,
          improvements in the processing of shipments, investment in the railway system to the
          rest of the european market and intensifying private invesments with public support to
          increase exports to Asia.
        </p>
        </section>
      <section id="outro">
        <!-- <p> -->
          <!-- <a href="#"> -->
            <!-- <md-icon class="md-size-4x">keyboard_arrow_up</md-icon> -->
          <!-- </a> -->
        <!-- </p> -->
        <!-- <br /> -->
        <p>
          Research and Design by <span style="font-family:dinpro">ARETIAN</span>
        </p>
        <p>
          &copy; Copyright 2019 - Aretian Urban Analytics and Design
        </p>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: "app",
  components: {
  },
};
</script>

<style lang="scss">
@import "assets/css/style.css";

body {
  font-family: Georgia, serif;
  font-size: 20px;
  line-height: 1.55em;
  background-color: #fffaf0 !important;
}

header {
  background-size: cover;
  background-position: center center;
  height: 800px;
}

header .intro-title-container {
  opacity: 0.8;
  position: absolute;
  top: 135px;
  left: 25%;
  width: 50%;
  z-index: 3;
  text-align: center;
  color: #3b3b3b;
  background-color: #fffaf0;
  padding: 4rem 2.5rem;
  font-family: dinpro, Roboto, Tahoma, Geneva;
}

.intro-title {
  font-family: dinpro, Roboto, Tahoma, Geneva;
  font-weight: 400;
  font-size: 4rem;
  line-height: 1em;
  text-align: center;
}

.intro-subtitle {
  z-index: 3;
  margin: 2rem auto 0 auto;
  font-size: 1.4rem;
  font-family: dinpro, Roboto, Tahoma, Geneva;
  line-height: 2rem;
}

header div.best-viewed {
  // opacity: 0.8;
  position: absolute;
  top: 710px;
  left: 25%;
  width: 50%;
  z-index: 3;
  text-align: center;
  // color: #3b3b3b;
  color:silver;
  // background-color: #fffaf0;
  // padding: 4rem 2.5rem;
  // font-family: dinpro, Roboto, Tahoma, Geneva;
  font-family:Arial, Helvetica, sans-serif;
  font-size:1rem;
}

span.company-name {
  font-family: dinpro, Roboto, Tahoma, Geneva;
}

p.company-name {
  z-index: 3;
  margin: 4rem auto 0.5rem auto;
  font-size: 2.7rem;
  font-family: dinpro, Roboto, Tahoma, Geneva;
  line-height: 2rem;
  letter-spacing: 0.7rem;
}

.company-description {
  z-index: 3;
  margin: 0 15px 0 auto;
  font-size: 1.2rem;
  font-family: dinpro, Roboto, Tahoma, Geneva;
  line-height: 2rem;
}

#hero-video {
  height: 760px;
  overflow: hidden;
  position: relative;
}

#video {
  z-index: -1;
  height: 100%;
  width: 100%;
  object-fit: cover;
}

#hero-video .overlay {
  width: 100%;
  height: 100%;
  background-color: rgba(249, 251, 253, 0.1);
  position: absolute;
}

section:first-of-type {
  margin-top: 4rem;
}

section {
  max-width: 40rem;
  margin: 1rem auto;
  text-align: left;
}

section h1 {
  font-size: 3rem;
  margin: 3rem auto 3rem auto;
  font-family: dinpro, Roboto, Tahoma, Geneva;
}

section h2 {
  font-size: 2.5rem;
  margin: 4rem auto 3rem auto;
  font-family: dinpro, Roboto, Tahoma, Geneva;
}

section p {
  margin-bottom: 2rem;
  font-family: Georgia, serif;
  font-size: 20px;
  line-height: 1.55em;
}

p.best-viewed {
  font-family:Arial, Helvetica, sans-serif;
  text-align:center;
}

p.author {
  font-family: dinpro, Roboto, Tahoma, Geneva;
  margin: 0 0 3rem 0;
}

section ul {
  font-family: Georgia, serif;
  font-size: 1.15rem;
}

section li {
  margin-bottom: 0.5rem;
}

article {
  position: relative;
  padding: 0 1rem;
  max-width: 30rem;
}

div .figure-container {
  display: table;
  text-align: center;
}

figure {
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  margin: 3rem 0;
  -webkit-transform: translate3d(0, 0, 0);
  -moz-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  text-align: center;
}

figure.display-figure {
  margin: 3rem 0;
}

figure p {
  text-align: center;
  padding: 1rem;
  position: absolute;
  top: 50%;
  left: 50%;
  -moz-transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  font-size: 8rem;
  font-weight: 900;
  color: #fff;
}

figcaption {
  font-size: 16px;
  text-align: center;
}
#logo {
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1003;
  background: transparent;
  text-decoration: none;
  padding: 40px 60px;
}

#logo a {
  text-decoration: none;
  border: 0;
  font-family: "Source Code Pro", "Helvetica Neue", Helvetica, Arial;
  font-size: 1.6em;
  color: gray;
  opacity: 0.5;
}
#logo a:hover {
  text-decoration: none;
  color: #c3c3c3 !important;
}
#outro {
  position: relative;
  background-color: #f3f3f3;
  padding: 2rem 1rem;
  max-width: 100%;
  width: 100%;
  height: 430px;
  text-align: center;
  vertical-align: text-bottom;
}

#outro p {
  padding: 2rem 0 3rem 0;
}

#outro a {
  text-decoration: none;
  color: #3b3b3b !important;
  border: none;
}

#outro a:hover {
  color: #3b3b3b !important;
}

a.author-link {
  text-decoration: none;
  border-width: 0.1px;
  color: #3b3b3b !important;
}

a:hover.author-link {
  color: #3b3b3b !important;
}

#outro > p {
  margin: 5rem auto 0 auto;
  color: gray;
}

#outro > p > a.author-link {
  /* margin: auto auto 0 auto; */
  color: gray;
}
</style>
